import { Download } from '@carbon/icons-react';
import { Button, FileUploaderButton, InlineLoading, InlineNotification, SkeletonText, Tile } from '@carbon/react';
import { asProblem, EmptyState, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import { createLocationAdminApi, type LocationImportResult } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { hoApi } from '../api';
import './locations.scss';

const admin = createLocationAdminApi(hoApi);
const RESULT_SHOWN_MS = 10_000;

/**
 * The location datasets (ADR 0026, FR-LOC-05, 06): import the Vietnamese provinces and wards that ship with the system, or a
 * newer file, into the master data every address and every store uses. Importing again updates what changed and closes what is
 * gone; nothing is deleted, and stores get the changes through sync.
 */
export function LocationDatasetsPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.locations.imports.manage');
  const queryClient = useQueryClient();
  const [result, setResult] = useState<LocationImportResult>();
  const datasets = useQuery({ queryKey: ['ho', 'location-datasets'], queryFn: () => admin.datasets() });
  const done = (r: LocationImportResult) => {
    setResult(r);
    void queryClient.invalidateQueries({ queryKey: ['ho', 'location-datasets'] });
  };
  const run = useMutation({
    mutationFn: (code: string) => admin.importDataset(code),
    onMutate: () => setResult(undefined),
    onSuccess: done,
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      let content: unknown;
      try {
        content = JSON.parse(await file.text());
      } catch {
        throw { title: t('locations.invalidFile') };
      }
      return admin.importFile(content as Parameters<typeof admin.importFile>[0]);
    },
    onMutate: () => setResult(undefined),
    onSuccess: done,
  });
  const importing = run.isPending || upload.isPending;
  const failure = run.error ?? upload.error ?? datasets.error;

  // The result is a confirmation, not a lasting message: it goes away by itself.
  useEffect(() => {
    if (!result) return undefined;
    const timer = window.setTimeout(() => setResult(undefined), RESULT_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [result]);

  return (
    <section className="pos-admin-page pos-locations">
      <PageHeader title={t('locations.title')} description={t('locations.intro')} />
      {failure && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(failure))}
        />
      )}
      {result && (
        <InlineNotification
          kind="success"
          role="status"
          lowContrast
          hideCloseButton
          title={t('locations.done', {
            created: Number(result.created),
            updated: Number(result.updated),
            closed: Number(result.closed),
          })}
        />
      )}
      {importing && <InlineLoading description={t('locations.importing')} />}
      {datasets.isLoading && <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />}
      {datasets.data?.length === 0 && (
        <EmptyState heading={t('admin.emptyTitle')} description={t('locations.noDatasets')} />
      )}
      {(datasets.data ?? []).map((d) => (
        <Tile key={d.code} className="pos-locations__card">
          <div>
            <h2 className="pos-locations__heading">
              {d.countryName} ({d.countryCode})
            </h2>
            <p>{t('locations.counts', { imported: Number(d.imported), total: Number(d.divisions) })}</p>
            {d.version && <p className="pos-locations__muted">{t('locations.version', { version: d.version })}</p>}
          </div>
          {canManage && (
            <Button renderIcon={Download} disabled={importing} onClick={() => run.mutate(d.code)}>
              {Number(d.imported) > 0 ? t('locations.update') : t('locations.import')}
            </Button>
          )}
        </Tile>
      ))}
      {canManage && (
        <div className="pos-locations__file">
          <FileUploaderButton
            labelText={t('locations.uploadFile')}
            buttonKind="tertiary"
            accept={['.json']}
            disabled={importing}
            disableLabelChanges
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
            }}
          />
          <p className="pos-locations__muted">{t('locations.fileHint')}</p>
        </div>
      )}
    </section>
  );
}
